import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ArrowDown,
  ArrowLeft,
  BarChart3,
  CalendarDays,
  CalendarRange,
  Check,
  CheckCircle2,
  CircleCheck,
  Download,
  FileDown,
  FileSpreadsheet,
  FileText,
  GitMerge,
  HardDrive,
  History,
  Lightbulb,
  Loader2,
  PlayCircle,
  Plane,
  RefreshCcw,
  ShieldCheck,
  SkipForward,
  Sliders,
  Trash2,
  Upload,
  UploadCloud,
  Users,
  XCircle,
  Zap,
} from "lucide-react";
import AttendanceBulkUpload from "./AttendanceBulkUpload";
import GuidedNotice from "../../ui/GuidedNotice";
import {
  resolveEmployeeDisplay,
} from "../../common/displayUtils";
import {
  deleteAttendanceUploadBatch,
  downloadAttendanceTemplate,
  downloadAttendanceErrorReport,
  fetchAttendanceUploadBatches,
  importAttendanceUpload,
  previewAttendanceUpload,
} from "../../../Moduels/attendance/attendanceUploadApi";

const IMPORT_MODES = [
  { id: "replace", label: "Replace existing", desc: "Overwrite attendance for the same employee + date" },
  { id: "skip", label: "Skip existing", desc: "Keep current records; only insert new dates" },
  { id: "merge", label: "Merge", desc: "Update check-in/out and remarks only on existing rows" },
];

const SOURCES = [
  { id: "excel_upload", label: "Excel upload" },
  { id: "biometric_import", label: "Biometric import" },
  { id: "api_integration", label: "API integration" },
];

const QUICK_STEPS = ["Choose File", "Preview", "Submit"];
const ADVANCED_STEPS = ["Upload File", "Validate", "Preview", "Import", "Complete"];

function pill(sev) {
  if (sev === "error") return "bg-red-50 text-red-800 border-red-200";
  if (sev === "warning") return "bg-amber-50 text-amber-900 border-amber-200";
  return "bg-emerald-50 text-emerald-800 border-emerald-200";
}

function StepIndicator({ steps, current }) {
  const items = [];
  steps.forEach((label, i) => {
    const num = i + 1;
    const isActive = num === current;
    const isDone = num < current;
    if (i > 0) {
      items.push(
        <div key={`line-${i}`} className={`h-[2px] flex-1 min-w-[2rem] mx-2 transition-colors ${isDone ? "bg-[#1a437d]" : "bg-slate-200"}`} />
      );
    }
    items.push(
      <div key={`step-${num}`} className="flex flex-col items-center gap-1.5 shrink-0">
        <div
          className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
            isActive
              ? "bg-[#0f2d52] text-white shadow-lg shadow-[#0f2d52]/30 ring-4 ring-[#0f2d52]/10"
              : isDone
                ? "bg-[#1a437d] text-white"
                : "bg-slate-100 text-slate-400 border-2 border-slate-200"
          }`}
        >
          {isDone ? <Check size={16} /> : num}
        </div>
        <span className={`text-[11px] font-semibold text-center leading-tight whitespace-nowrap ${isActive ? "text-[#0f2d52]" : isDone ? "text-[#1a437d]" : "text-slate-400"}`}>
          {label}
        </span>
      </div>
    );
  });
  return (
    <div className="flex items-center py-6 px-6 md:px-10 lg:px-16">
      {items}
    </div>
  );
}

function FileFormatBadges({ maxSize = "5 MB" }) {
  return (
    <div className="mt-3 pt-3 border-t border-slate-200 space-y-2">
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">Supported Files</span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-100 border border-emerald-300 text-[11px] font-bold text-emerald-800">
          <FileSpreadsheet size={12} /> Excel (.xlsx, .xls)
        </span>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-blue-100 border border-blue-300 text-[11px] font-bold text-blue-800">
          <FileText size={12} /> CSV (.csv)
        </span>
      </div>
      <div className="flex items-center gap-1.5">
        <HardDrive size={12} className="text-amber-700" />
        <span className="text-[11px] font-semibold text-slate-700">Maximum Upload Size:</span>
        <span className="inline-flex items-center px-2 py-0.5 rounded bg-amber-200 border border-amber-400 text-[11px] font-extrabold text-amber-900">{maxSize}</span>
      </div>
    </div>
  );
}

export default function AttendanceUpload({ embedded = false }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const inputRef = useRef(null);

  const [mode, setMode] = useState(null);
  const [quickStage, setQuickStage] = useState("select");
  const [showQuickTemplate, setShowQuickTemplate] = useState(true);
  const [showAdvancedTemplate, setShowAdvancedTemplate] = useState(true);

  const [file, setFile] = useState(null);
  const [uploadType, setUploadType] = useState("auto");
  const [importMode, setImportMode] = useState("replace");
  const [attendanceSource, setAttendanceSource] = useState("excel_upload");
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [stage, setStage] = useState("select");
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [batches, setBatches] = useState([]);
  const [advancedSuccessMsg, setAdvancedSuccessMsg] = useState("");

  const loadBatches = useCallback(async () => {
    try {
      const data = await fetchAttendanceUploadBatches(8);
      setBatches(data || []);
    } catch {
      setBatches([]);
    }
  }, []);

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  const deleteBatch = async (batchId) => {
    if (!window.confirm("Are you sure you want to delete this import record?")) return;
    try {
      await deleteAttendanceUploadBatch(batchId);
      setBatches((prev) => prev.filter((b) => b.batch_id !== batchId));
    } catch {
      setError("Failed to delete batch");
    }
  };

  const quickStep = useMemo(() => {
    if (quickStage === "select") return 1;
    if (quickStage === "preview") return 2;
    return 3;
  }, [quickStage]);

  const advancedStep = useMemo(() => {
    if (stage === "select") return 1;
    if (stage === "configure") return 2;
    if (stage === "preview") return 3;
    if (stage === "importing") return 4;
    return 5;
  }, [stage]);

  const onPick = (f) => {
    if (!f) return;
    const name = f.name.toLowerCase();
    if (!name.endsWith(".csv") && !name.endsWith(".xlsx")) {
      setError("Only .xlsx and .csv files are supported");
      return;
    }
    setFile(f);
    setError("");
    setPreview(null);
    setResult(null);
    setStage("configure");
  };

  const runPreview = async () => {
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      const [data] = await Promise.all([
        previewAttendanceUpload(file, {
          upload_type: uploadType,
          import_mode: importMode,
          attendance_source: attendanceSource,
          year: uploadType === "monthly" ? year : undefined,
          month: uploadType === "monthly" ? month : undefined,
        }),
        new Promise((r) => setTimeout(r, 3600)),
      ]);
      setPreview(data);
      setStage("preview");
    } catch (e) {
      setError(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : e?.response?.data?.detail?.message || e.message || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const runImport = async () => {
    if (!file) return;
    setBusy(true);
    setError("");
    setAdvancedSuccessMsg("");
    setStage("importing");
    try {
      const [data] = await Promise.all([
        importAttendanceUpload(file, {
          upload_type: uploadType,
          import_mode: importMode,
          attendance_source: attendanceSource,
          year: uploadType === "monthly" ? year : undefined,
          month: uploadType === "monthly" ? month : undefined,
        }),
        new Promise((r) => setTimeout(r, 4000)),
      ]);
      setResult(data);
      setStage("result");
      setBusy(false);
      loadBatches();
      const successCount = (data?.success_count || 0);
      const failedCount = (data?.failed_count || 0);
      setAdvancedSuccessMsg(`Successfully imported ${successCount} attendance record(s). Failed: ${failedCount}`);
      const importedRows = (preview?.rows || []).filter(
        (r) => r.severity !== "error" && r.employee_id && r.work_date
      );
      if (importedRows.length) {
        setTimeout(() => {
          openDailyCalendar(importedRows);
        }, 5500);
      }
    } catch (e) {
      setError(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : e?.response?.data?.detail?.message || e.message || "Import failed");
      setStage("preview");
      setBusy(false);
    }
  };

  const dash = result?.dashboard || preview?.dashboard_preview || {};

  const openDailyCalendar = (rows = []) => {
    const first = rows.find?.((r) => r.employee_id && r.work_date) || rows[0];
    const next = new URLSearchParams(searchParams);
    next.set("tab", "daily");
    if (first?.work_date) next.set("month", String(first.work_date).slice(0, 7));
    if (first?.employee_id) next.set("employee", String(first.employee_id));
    setSearchParams(next, { replace: true });
  };

  const resetAdvanced = () => {
    setFile(null);
    setStage("select");
    setPreview(null);
    setResult(null);
    setError("");
    setAdvancedSuccessMsg("");
  };

  const goBack = () => {
    setMode(null);
    resetAdvanced();
    setQuickStage("select");
    setShowQuickTemplate(true);
    setShowAdvancedTemplate(true);
  };

  return (
    <div className={embedded ? "att-embedded w-full" : "min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100/30 p-4 md:p-6"}>
      <div className="w-full max-w-full min-w-0 space-y-5">

        {/* ── LANDING VIEW ─────────────────────────── */}
        {mode === null && (
          <>
            {/* Two equal-size option cards */}
            <div className="grid md:grid-cols-2 gap-5">
              {/* Quick Upload Card */}
              <div className="relative bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden hover:shadow-md transition group flex flex-col">
                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-sky-500 to-blue-600" />
                <div className="p-5 md:p-6 flex flex-col flex-1">
                  <span className="inline-block text-[10px] uppercase tracking-wider font-bold text-sky-700 bg-sky-50 border border-sky-200 rounded-full px-2.5 py-0.5 mb-3 self-start">
                    Fast &amp; Simple
                  </span>
                  <div className="flex items-start gap-3 mb-4">
                    <div className="shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 text-white flex items-center justify-center shadow-sm">
                      <Zap size={20} />
                    </div>
                    <div>
                      <h3 className="font-bold text-base text-slate-900">Daily Attendance Upload</h3>
                      <p className="text-xs text-slate-500 mt-0.5">Fast import for daily attendance corrections</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-slate-600 mb-5 flex-1">
                    <li className="flex items-center gap-2"><Check size={14} className="text-emerald-600 shrink-0" /> Upload CSV or Excel file</li>
                    <li className="flex items-center gap-2"><Check size={14} className="text-emerald-600 shrink-0" /> Instant preview in browser</li>
                    <li className="flex items-center gap-2"><Check size={14} className="text-emerald-600 shrink-0" /> Client-side processing</li>
                    <li className="flex items-center gap-2"><Check size={14} className="text-emerald-600 shrink-0" /> Best for daily updates &amp; small files</li>
                    <li className="flex items-center gap-2"><Check size={14} className="text-emerald-600 shrink-0" /> Supports check-in/out columns</li>
                  </ul>
                  <div className="mt-4 px-4 py-3 bg-slate-50 rounded-xl border border-slate-200">
                    <div className="flex items-center gap-1.5 mb-3">
                      <Zap size={13} className="text-sky-600" />
                      <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">Quick Upload Process</span>
                    </div>
                    <div className="flex items-center justify-between gap-2 px-2">
                      <div className="flex flex-col items-center gap-1">
                        <div className="w-7 h-7 rounded-full bg-sky-100 border border-sky-200 flex items-center justify-center text-[11px] font-bold text-sky-700">1</div>
                        <span className="text-[10px] font-semibold text-slate-600">Choose File</span>
                      </div>
                      <ArrowDown size={12} className="text-slate-300 rotate-[-90deg]" />
                      <div className="flex flex-col items-center gap-1">
                        <div className="w-7 h-7 rounded-full bg-sky-100 border border-sky-200 flex items-center justify-center text-[11px] font-bold text-sky-700">2</div>
                        <span className="text-[10px] font-semibold text-slate-600">Preview</span>
                      </div>
                      <ArrowDown size={12} className="text-slate-300 rotate-[-90deg]" />
                      <div className="flex flex-col items-center gap-1">
                        <div className="w-7 h-7 rounded-full bg-sky-100 border border-sky-200 flex items-center justify-center text-[11px] font-bold text-sky-700">3</div>
                        <span className="text-[10px] font-semibold text-slate-600">Submit</span>
                      </div>
                      <ArrowDown size={12} className="text-slate-300 rotate-[-90deg]" />
                      <div className="flex flex-col items-center gap-1">
                        <div className="w-7 h-7 rounded-full bg-emerald-100 border border-emerald-200 flex items-center justify-center">
                          <CircleCheck size={14} className="text-emerald-600" />
                        </div>
                        <span className="text-[10px] font-bold text-emerald-700">Done</span>
                      </div>
                    </div>
                  </div>
                  <FileFormatBadges />
                  <button
                    type="button"
                    onClick={() => setMode("quick")}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-sky-500 to-blue-600 shadow-md hover:shadow-lg hover:-translate-y-0.5 transition mt-4"
                  >
                    <Upload size={16} />
                    Start Daily Upload
                  </button>
                </div>
              </div>

              {/* Advanced Import Card */}
              <div className="relative bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden hover:shadow-md transition group flex flex-col">
                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#0f2d52] via-[#1a437d] to-[#2a5fa3]" />
                <div className="p-5 md:p-6 flex flex-col flex-1">
                  <span className="inline-block text-[10px] uppercase tracking-wider font-bold text-[#1a437d] bg-blue-50 border border-blue-200 rounded-full px-2.5 py-0.5 mb-3 self-start">
                    Advanced &amp; Audit Ready
                  </span>
                  <div className="flex items-start gap-3 mb-4">
                    <div className="shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-[#0f2d52] to-[#2a5fa3] text-white flex items-center justify-center shadow-sm">
                      <ShieldCheck size={20} />
                    </div>
                    <div>
                      <h3 className="font-bold text-base text-slate-900">Advanced Import with Audit</h3>
                      <p className="text-xs text-slate-500 mt-0.5">Enterprise import with validation and audit tracking</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-slate-600 mb-5 flex-1">
                    <li className="flex items-center gap-2"><Check size={14} className="text-emerald-600 shrink-0" /> Daily &amp; Monthly templates supported</li>
                    <li className="flex items-center gap-2"><Check size={14} className="text-emerald-600 shrink-0" /> Server-side processing with validation</li>
                    <li className="flex items-center gap-2"><Check size={14} className="text-emerald-600 shrink-0" /> Duplicate detection &amp; data validation</li>
                    <li className="flex items-center gap-2"><Check size={14} className="text-emerald-600 shrink-0" /> Import modes: Replace, Skip, Merge</li>
                    <li className="flex items-center gap-2"><Check size={14} className="text-emerald-600 shrink-0" /> Full audit log &amp; batch history</li>
                  </ul>
                  <div className="mt-4 p-3 bg-slate-50 rounded-xl border border-slate-200">
                    <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider block mb-2.5">Advanced Features</span>
                    <div className="grid grid-cols-4 gap-2">
                      <div className="flex flex-col items-center gap-1.5 text-center">
                        <div className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-[#1a437d]">
                          <FileDown size={15} />
                        </div>
                        <span className="text-[10px] font-semibold text-slate-600 leading-tight">Template Support</span>
                      </div>
                      <div className="flex flex-col items-center gap-1.5 text-center">
                        <div className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-[#1a437d]">
                          <ShieldCheck size={15} />
                        </div>
                        <span className="text-[10px] font-semibold text-slate-600 leading-tight">Validation & Warnings</span>
                      </div>
                      <div className="flex flex-col items-center gap-1.5 text-center">
                        <div className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-[#1a437d]">
                          <GitMerge size={15} />
                        </div>
                        <span className="text-[10px] font-semibold text-slate-600 leading-tight">Import Modes (Replace/Skip/Merge)</span>
                      </div>
                      <div className="flex flex-col items-center gap-1.5 text-center">
                        <div className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-[#1a437d]">
                          <History size={15} />
                        </div>
                        <span className="text-[10px] font-semibold text-slate-600 leading-tight">Audit Trail & History</span>
                      </div>
                    </div>
                  </div>
                  <FileFormatBadges maxSize="10 MB" />
                  <button
                    type="button"
                    onClick={() => setMode("advanced")}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-[#0f2d52] via-[#1a437d] to-[#2a5fa3] shadow-md hover:shadow-lg hover:-translate-y-0.5 transition mt-4"
                  >
                    <Upload size={16} />
                    Start Advanced Import
                  </button>
                </div>
              </div>
            </div>

            {/* Quick Import Tips - full width horizontal */}
            <div className="relative bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-amber-400 to-orange-500" />
              <div className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center">
                    <Lightbulb size={16} />
                  </div>
                  <h4 className="font-bold text-sm text-slate-900">Quick Import Tips</h4>
                </div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2.5">
                  <div className="flex items-start gap-2 text-sm text-slate-600"><Check size={14} className="text-emerald-600 shrink-0 mt-0.5" /> Use the correct template to avoid errors</div>
                  <div className="flex items-start gap-2 text-sm text-slate-600"><Check size={14} className="text-emerald-600 shrink-0 mt-0.5" /> Do not change column headers</div>
                  <div className="flex items-start gap-2 text-sm text-slate-600"><Check size={14} className="text-emerald-600 shrink-0 mt-0.5" /> Employee codes must already exist</div>
                  <div className="flex items-start gap-2 text-sm text-slate-600"><Check size={14} className="text-emerald-600 shrink-0 mt-0.5" /> Dates should be in YYYY-MM-DD format</div>
                  <div className="flex items-start gap-2 text-sm text-slate-600"><Check size={14} className="text-emerald-600 shrink-0 mt-0.5" /> Save file as .xlsx or .csv only</div>
                  <div className="flex items-start gap-2 text-sm text-slate-600"><Check size={14} className="text-emerald-600 shrink-0 mt-0.5" /> Check-in/out in 24-hour format (09:00, 18:00)</div>
                </div>
              </div>
            </div>

            {/* Recent History - full width */}
            <div className="relative bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-teal-500 to-cyan-600" />
              <div className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-start gap-3">
                    <div className="shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 text-white flex items-center justify-center shadow-sm">
                      <History size={18} />
                    </div>
                    <div>
                      <h3 className="font-bold text-sm text-slate-900">Recent Import History</h3>
                      <p className="text-xs text-slate-500">Last imports for traceability.</p>
                    </div>
                  </div>
                  <button type="button" onClick={loadBatches} className="text-xs font-semibold text-teal-700 hover:text-teal-900 hover:underline inline-flex items-center gap-1">
                    <RefreshCcw size={12} />Refresh
                  </button>
                </div>
                {batches.length > 0 ? (
                  <div className="overflow-x-auto -mx-1 px-1">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-slate-50 border-y border-slate-100">
                          <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider font-bold text-slate-500">File Name</th>
                          <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider font-bold text-slate-500">Type</th>
                          <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider font-bold text-slate-500">Rows</th>
                          <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider font-bold text-slate-500">Valid</th>
                          <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider font-bold text-slate-500">Failed</th>
                          <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider font-bold text-slate-500">Imported By</th>
                          <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider font-bold text-slate-500">Imported On</th>
                          <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider font-bold text-slate-500">Status</th>
                          <th className="text-right px-3 py-2.5 text-[10px] uppercase tracking-wider font-bold text-slate-500">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {batches.map((b) => {
                          const failed = (b.total_records || 0) - (b.success_count || 0);
                          const statusOk = failed === 0;
                          const partial = failed > 0 && (b.success_count || 0) > 0;
                          return (
                            <tr key={b.batch_id} className="border-b border-slate-50 hover:bg-slate-50/50 transition">
                              <td className="px-3 py-2.5 font-medium text-slate-700">{b.original_filename || "upload"}</td>
                              <td className="px-3 py-2.5 text-slate-500">{b.upload_type || "—"}</td>
                              <td className="px-3 py-2.5 text-slate-700 font-semibold">{b.total_records ?? "—"}</td>
                              <td className="px-3 py-2.5 text-emerald-700 font-semibold">{b.success_count ?? "—"}</td>
                              <td className={`px-3 py-2.5 font-semibold ${failed > 0 ? "text-red-600" : "text-slate-400"}`}>{failed}</td>
                              <td className="px-3 py-2.5 text-slate-500">{b.uploaded_by_name || "—"}</td>
                              <td className="px-3 py-2.5 text-slate-500">{b.uploaded_at ? new Date(b.uploaded_at).toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                              <td className="px-3 py-2.5">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                                  statusOk ? "bg-emerald-50 text-emerald-700 border-emerald-200" : partial ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-red-50 text-red-700 border-red-200"
                                }`}>
                                  {statusOk ? "Success" : partial ? "Partial" : "Failed"}
                                </span>
                              </td>
                              <td className="px-3 py-2.5">
                                <div className="flex items-center justify-end gap-1.5">
                                  {b.batch_id && failed > 0 && (
                                    <button type="button" onClick={() => downloadAttendanceErrorReport(b.batch_id)} className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-red-50 flex items-center justify-center text-slate-500 hover:text-red-600 transition" title="Download error report">
                                      <Download size={13} />
                                    </button>
                                  )}
                                  <button type="button" onClick={() => deleteBatch(b.batch_id)} className="w-7 h-7 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center text-red-500 hover:text-red-700 transition" title="Delete">
                                    <Trash2 size={13} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-400">
                    <History size={24} className="mx-auto mb-2 text-slate-300" />
                    <p className="text-sm">No import history yet.</p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* ── QUICK UPLOAD VIEW ──────────────────────── */}
        {mode === "quick" && (
          <>
            <div className="flex items-center gap-3 mb-1">
              <button type="button" onClick={goBack} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-sky-600 to-blue-700 hover:from-sky-700 hover:to-blue-800 shadow-sm hover:shadow-md transition">
                <ArrowLeft size={16} /> Change upload type
              </button>
              <h2 className="text-lg font-bold text-slate-900">Daily Attendance Upload</h2>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
              <StepIndicator steps={QUICK_STEPS} current={quickStep} />
            </div>

            {/* Template question before upload */}
            {showQuickTemplate && quickStage === "select" && (
              <div className="relative bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-500 to-teal-600" />
                <div className="p-5">
                  <h3 className="font-bold text-sm text-slate-900 mb-1">Do you have a template ready?</h3>
                  <p className="text-xs text-slate-500 mb-4">If you don't have a file yet, download the daily template below, fill it with your attendance data, and upload it.</p>

                  <div className="bg-slate-50 rounded-xl border border-slate-100 p-4 mb-4">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white flex items-center justify-center shadow-sm">
                        <CalendarDays size={18} />
                      </div>
                      <div>
                        <h4 className="font-bold text-sm text-slate-800">Daily Attendance Template</h4>
                        <p className="text-xs text-slate-500 mt-0.5">One row per employee with date, status, check-in, check-out, and hours.</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button type="button" className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg border-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50 transition" onClick={() => downloadAttendanceTemplate("daily", { filled: false })}>
                        <FileDown size={13} /> Download Template
                      </button>
                      <button type="button" className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-sm hover:shadow-md transition" onClick={() => downloadAttendanceTemplate("daily", { filled: true })}>
                        <FileText size={13} /> Download Example
                      </button>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowQuickTemplate(false)}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-sky-500 to-blue-600 shadow-md hover:shadow-lg transition"
                  >
                    <SkipForward size={16} />
                    I already have a file, skip to upload
                  </button>
                </div>
              </div>
            )}

            {/* Show upload after template dismissed */}
            {(!showQuickTemplate || quickStage !== "select") && (
              <AttendanceBulkUpload
                embedded
                onUploadSuccess={openDailyCalendar}
                onStageChange={(s) => setQuickStage(s)}
              />
            )}
          </>
        )}

        {/* ── ADVANCED IMPORT VIEW ───────────────────── */}
        {mode === "advanced" && (
          <>
            <div className="flex items-center gap-3 mb-1">
              <button type="button" onClick={goBack} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-sky-600 to-blue-700 hover:from-sky-700 hover:to-blue-800 shadow-sm hover:shadow-md transition">
                <ArrowLeft size={16} /> Change upload type
              </button>
              <h2 className="text-lg font-bold text-slate-900">Advanced Import with Audit</h2>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
              <StepIndicator steps={ADVANCED_STEPS} current={advancedStep} />
            </div>

            {/* Template question before upload */}
            {showAdvancedTemplate && stage === "select" && (
              <div className="relative bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#0f2d52] via-[#1a437d] to-[#2a5fa3]" />
                <div className="p-5">
                  <h3 className="font-bold text-sm text-slate-900 mb-1">Do you have a template ready?</h3>
                  <p className="text-xs text-slate-500 mb-4">Choose the right template for your data. Download it, fill in your attendance records, and then upload.</p>

                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    {/* Daily template */}
                    <div className="bg-slate-50 rounded-xl border border-slate-100 p-4">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white flex items-center justify-center shadow-sm">
                          <CalendarDays size={18} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-bold text-sm text-slate-800">Daily Template</h4>
                            <span className="text-[9px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">Recommended</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5">One row per employee per day.</p>
                        </div>
                      </div>
                      <ul className="text-xs text-slate-500 space-y-1 mb-3 ml-1">
                        <li>Includes check-in, check-out, status, hours</li>
                        <li>Ideal for daily attendance corrections</li>
                      </ul>
                      <div className="flex flex-wrap gap-2">
                        <button type="button" className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg border-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50 transition" onClick={() => downloadAttendanceTemplate("daily", { filled: false })}>
                          <FileDown size={13} /> Download Template
                        </button>
                        <button type="button" className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-sm hover:shadow-md transition" onClick={() => downloadAttendanceTemplate("daily", { filled: true })}>
                          <FileText size={13} /> Download Example
                        </button>
                      </div>
                    </div>

                    {/* Monthly template */}
                    <div className="bg-slate-50 rounded-xl border border-slate-100 p-4">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-600 text-white flex items-center justify-center shadow-sm">
                          <CalendarRange size={18} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-bold text-sm text-slate-800">Monthly Template</h4>
                            <span className="text-[9px] uppercase tracking-wider font-bold text-violet-700 bg-violet-50 border border-violet-200 rounded-full px-2 py-0.5">Bulk</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5">One row per employee, dates as columns.</p>
                        </div>
                      </div>
                      <ul className="text-xs text-slate-500 space-y-1 mb-3 ml-1">
                        <li>Full month data in a single file</li>
                        <li>Ideal for biometric exports &amp; bulk imports</li>
                      </ul>
                      <div className="flex flex-wrap gap-2">
                        <button type="button" className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg border-2 border-violet-300 text-violet-700 hover:bg-violet-50 transition" onClick={() => downloadAttendanceTemplate("monthly", { filled: false })}>
                          <FileDown size={13} /> Download Template
                        </button>
                        <button type="button" className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg bg-gradient-to-r from-violet-500 to-fuchsia-600 text-white shadow-sm hover:shadow-md transition" onClick={() => downloadAttendanceTemplate("monthly", { filled: true })}>
                          <FileText size={13} /> Download Example
                        </button>
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowAdvancedTemplate(false)}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-[#0f2d52] via-[#1a437d] to-[#2a5fa3] shadow-md hover:shadow-lg transition"
                  >
                    <SkipForward size={16} />
                    I already have a file, skip to upload
                  </button>
                </div>
              </div>
            )}

            {/* Show upload after template dismissed */}
            {(!showAdvancedTemplate || stage !== "select") && (
              <>
                {/* Step 1: Upload File */}
                {stage === "select" && (
                  <div
                    className="relative border-2 border-dashed border-amber-300 rounded-2xl bg-gradient-to-br from-amber-50/70 via-orange-50/40 to-white p-8 text-center cursor-pointer hover:border-amber-500 transition group"
                    onClick={() => inputRef.current?.click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); onPick(e.dataTransfer.files?.[0]); }}
                  >
                    <input ref={inputRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={(e) => onPick(e.target.files?.[0])} />
                    <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 text-white flex items-center justify-center shadow-md group-hover:scale-110 transition">
                      <UploadCloud size={26} />
                    </div>
                    <p className="text-slate-800 font-semibold mt-3">Drag &amp; drop Excel or CSV here</p>
                    <p className="text-xs text-slate-500 mt-1">or click to browse</p>
                    <p className="text-[10px] text-slate-400 mt-2">Supports .xlsx, .csv (max 10 MB)</p>
                  </div>
                )}

                {/* Step 2: Configure & Validate */}
                {(stage === "configure" || stage === "preview" || stage === "importing" || stage === "result") && (
                  <div className="relative bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-cyan-500 to-sky-600" />
                    <div className="p-5">
                      <div className="flex items-start gap-3 mb-4">
                        <div className="shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-sky-600 text-white flex items-center justify-center shadow-sm">
                          <Sliders size={18} />
                        </div>
                        <div>
                          <h3 className="font-bold text-sm text-slate-900">Configure import</h3>
                          <p className="text-xs text-slate-500">Choose how this file should be interpreted and imported.</p>
                        </div>
                      </div>
                      {file && (
                        <p className="mb-3 inline-flex items-center gap-1.5 text-sm text-amber-800 font-semibold bg-amber-100 px-3 py-1 rounded-full border border-amber-200">
                          <FileText size={14} /> {file.name}
                        </p>
                      )}
                      <div className="grid md:grid-cols-3 gap-4">
                        <label className="text-sm">
                          <span className="text-slate-600 block mb-1 font-medium">Sheet type</span>
                          <select className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 outline-none" value={uploadType} onChange={(e) => setUploadType(e.target.value)}>
                            <option value="auto">Auto-detect</option>
                            <option value="daily">Daily rows</option>
                            <option value="monthly">Monthly grid</option>
                          </select>
                        </label>
                        <label className="text-sm">
                          <span className="text-slate-600 block mb-1 font-medium">Import mode</span>
                          <select className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 outline-none" value={importMode} onChange={(e) => setImportMode(e.target.value)}>
                            {IMPORT_MODES.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                          </select>
                        </label>
                        <label className="text-sm">
                          <span className="text-slate-600 block mb-1 font-medium">Attendance source</span>
                          <select className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 outline-none" value={attendanceSource} onChange={(e) => setAttendanceSource(e.target.value)}>
                            {SOURCES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                          </select>
                        </label>
                        {uploadType === "monthly" && (
                          <>
                            <label className="text-sm">
                              <span className="text-slate-600 block mb-1 font-medium">Year</span>
                              <input type="number" className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 outline-none" value={year} onChange={(e) => setYear(Number(e.target.value))} />
                            </label>
                            <label className="text-sm">
                              <span className="text-slate-600 block mb-1 font-medium">Month</span>
                              <input type="number" min={1} max={12} className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 outline-none" value={month} onChange={(e) => setMonth(Number(e.target.value))} />
                            </label>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {error && (
                  <GuidedNotice domain="attendance" message={error} onDismiss={() => setError("")} />
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3">
                  {file && stage !== "result" && stage !== "importing" && (
                    <button type="button" disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold bg-gradient-to-r from-indigo-500 to-blue-600 text-white shadow-md hover:shadow-xl hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0 transition" onClick={runPreview}>
                      {busy ? <Loader2 size={16} className="animate-spin" /> : <PlayCircle size={16} />}
                      {busy ? "Validating..." : "Validate & preview"}
                    </button>
                  )}
                  {preview && stage === "preview" && (
                    <button type="button" disabled={busy || preview.valid_count === 0} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-md hover:shadow-xl hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0 transition" onClick={runImport}>
                      {busy ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                      {busy ? "Importing..." : `Import ${preview.valid_count} valid rows`}
                    </button>
                  )}
                  {(stage === "configure" || stage === "preview") && (
                    <button type="button" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border-2 border-slate-300 text-slate-600 hover:bg-slate-50 transition" onClick={() => { resetAdvanced(); inputRef.current?.click(); }}>
                      <UploadCloud size={14} /> Change file
                    </button>
                  )}
                  <button type="button" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border-2 border-rose-300 text-rose-700 hover:bg-rose-50 hover:border-rose-400 transition" onClick={() => { loadBatches(); }}>
                    <RefreshCcw size={14} /> Refresh audit log
                  </button>
                </div>

                {/* Importing loading state (Step 4) */}
                {stage === "importing" && (
                  <div className="relative bg-white rounded-2xl border border-blue-200 shadow-sm overflow-hidden">
                    <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-500 to-indigo-600" />
                    <div className="p-6 flex flex-col items-center gap-3">
                      <Loader2 size={32} className="animate-spin text-blue-600" />
                      <span className="text-sm text-blue-700 font-semibold">Importing attendance records, please wait...</span>
                      <span className="text-xs text-slate-500">This may take a few moments depending on the file size.</span>
                    </div>
                  </div>
                )}

                {/* Complete step (Step 5) - Success message with redirect */}
                {stage === "result" && advancedSuccessMsg && (
                  <div className="relative bg-white rounded-2xl border border-emerald-200 shadow-sm overflow-hidden">
                    <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-500 to-green-600" />
                    <div className="p-5">
                      <div className="flex items-start gap-3">
                        <div className="shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 text-white flex items-center justify-center shadow-sm">
                          <CheckCircle2 size={18} />
                        </div>
                        <div>
                          <h3 className="font-bold text-sm text-emerald-800">{advancedSuccessMsg}</h3>
                          <span className="text-xs text-emerald-600 mt-2 inline-flex items-center gap-1.5">
                            <Loader2 size={12} className="animate-spin" /> Redirecting to Attendance Calendar...
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Validation Preview */}
                {preview && (
                  <div className="relative bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-indigo-500 to-purple-600" />
                    <div className="p-5">
                      <div className="flex items-start gap-3 mb-4">
                        <div className="shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white flex items-center justify-center shadow-sm">
                          <ShieldCheck size={18} />
                        </div>
                        <div>
                          <h3 className="font-bold text-sm text-slate-900">Validation preview</h3>
                          <p className="text-xs text-slate-500">Inspect parsed rows before committing the import.</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 text-sm mb-4">
                        <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-700 font-semibold border border-slate-200">Total: {preview.total_records}</span>
                        <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 font-semibold border border-emerald-200">Valid: {preview.valid_count}</span>
                        <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-900 font-semibold border border-amber-200">Warnings: {preview.warning_count}</span>
                        <span className="px-3 py-1 rounded-full bg-red-100 text-red-800 font-semibold border border-red-200">Errors: {preview.error_count}</span>
                        <span className="px-3 py-1 rounded-full bg-violet-100 text-violet-800 font-semibold border border-violet-200">Duplicates: {preview.duplicate_count}</span>
                      </div>
                      <div className="max-h-64 overflow-auto border-2 border-indigo-100 rounded-lg">
                        <table className="w-full text-xs">
                          <thead className="bg-gradient-to-r from-indigo-50 to-purple-50 sticky top-0">
                            <tr>
                              <th className="text-left p-2 text-indigo-900 font-semibold">Row</th>
                              <th className="text-left p-2 text-indigo-900 font-semibold">Employee</th>
                              <th className="text-left p-2 text-indigo-900 font-semibold">Code</th>
                              <th className="text-left p-2 text-indigo-900 font-semibold">Date</th>
                              <th className="text-left p-2 text-indigo-900 font-semibold">Status</th>
                              <th className="text-left p-2 text-indigo-900 font-semibold">Result</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(preview.rows || []).slice(0, 200).map((r) => (
                              <tr key={`${r.row_index}-${r.work_date}`} className="border-t hover:bg-indigo-50/30">
                                <td className="p-2">{r.row_index}</td>
                                <td className="p-2">{resolveEmployeeDisplay(r)}</td>
                                <td className="p-2">{r.employee_code || "—"}</td>
                                <td className="p-2">{r.work_date}</td>
                                <td className="p-2">{r.status_raw} → {r.status_normalized}</td>
                                <td className="p-2">
                                  <span className={`px-2 py-0.5 rounded border ${pill(r.severity)}`}>{r.severity}</span>
                                  {r.messages?.length > 0 && <span className="block text-slate-500 mt-0.5">{r.messages.join("; ")}</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {/* Summary Dashboard */}
                {(result || preview) && (
                  <div className="relative bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-pink-500 to-rose-600" />
                    <div className="p-5">
                      <div className="flex items-start gap-3 mb-4">
                        <div className="shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 text-white flex items-center justify-center shadow-sm">
                          <BarChart3 size={18} />
                        </div>
                        <div>
                          <h3 className="font-bold text-sm text-slate-900">Summary dashboard</h3>
                          <p className="text-xs text-slate-500">Quick overview of what the upload affects.</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <StatCard icon={<Users size={18} />} label="Employees" value={dash.total_employees} bg="from-blue-50 to-sky-50" iconBg="from-blue-500 to-sky-500" border="border-blue-200" />
                        <StatCard icon={<CheckCircle2 size={18} />} label="Present" value={dash.present_count} bg="from-emerald-50 to-teal-50" iconBg="from-emerald-500 to-teal-500" border="border-emerald-200" />
                        <StatCard icon={<XCircle size={18} />} label="Absent" value={dash.absent_count} bg="from-rose-50 to-red-50" iconBg="from-rose-500 to-red-500" border="border-rose-200" />
                        <StatCard icon={<Plane size={18} />} label="Leave" value={dash.leave_count} bg="from-amber-50 to-orange-50" iconBg="from-amber-500 to-orange-500" border="border-amber-200" />
                      </div>
                      {result && (
                        <div className="mt-4 text-sm text-slate-700 space-y-1 bg-slate-50 rounded-lg p-3 border border-slate-200">
                          <p>Imported: <strong className="text-emerald-700">{result.success_count}</strong> · Failed: <strong className="text-red-700">{result.failed_count}</strong> · Skipped: <strong className="text-amber-700">{result.skipped_count}</strong></p>
                          <p>Source: <strong>{result.attendance_source}</strong> · Mode: <strong>{result.import_mode}</strong></p>
                          {result.failed_count > 0 && result.batch_id && (
                            <button type="button" className="inline-flex items-center gap-1.5 mt-1 text-rose-600 hover:text-rose-800 underline font-semibold" onClick={() => downloadAttendanceErrorReport(result.batch_id)}>
                              <FileDown size={14} /> Download error report
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Audit Log */}
                {batches.length > 0 && (
                  <div className="relative bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-teal-500 to-cyan-600" />
                    <div className="p-5">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 text-white flex items-center justify-center shadow-sm">
                          <History size={18} />
                        </div>
                        <div>
                          <h3 className="font-bold text-sm text-slate-900">Recent uploads (audit)</h3>
                          <p className="text-xs text-slate-500">Last few imports for traceability.</p>
                        </div>
                      </div>
                      <div className="overflow-x-auto -mx-1 px-1">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-slate-50 border-y border-slate-100">
                              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider font-bold text-slate-500">File Name</th>
                              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider font-bold text-slate-500">Type</th>
                              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider font-bold text-slate-500">Source</th>
                              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider font-bold text-slate-500">Rows</th>
                              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider font-bold text-slate-500">Success</th>
                              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider font-bold text-slate-500">Uploaded By</th>
                              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider font-bold text-slate-500">Uploaded On</th>
                              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider font-bold text-slate-500">Status</th>
                              <th className="text-right px-3 py-2.5 text-[10px] uppercase tracking-wider font-bold text-slate-500">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {batches.map((b) => {
                              const failed = (b.total_records || 0) - (b.success_count || 0);
                              const statusOk = failed === 0;
                              const partial = failed > 0 && (b.success_count || 0) > 0;
                              return (
                                <tr key={b.batch_id} className="border-b border-slate-50 hover:bg-teal-50/40 transition">
                                  <td className="px-3 py-2.5 font-medium text-slate-700">{b.original_filename || "upload"}</td>
                                  <td className="px-3 py-2.5 text-slate-500">{b.upload_type || "—"}</td>
                                  <td className="px-3 py-2.5 text-slate-500">{b.attendance_source || "—"}</td>
                                  <td className="px-3 py-2.5 text-slate-700 font-semibold">{b.total_records ?? "—"}</td>
                                  <td className="px-3 py-2.5 text-emerald-700 font-semibold">{b.success_count ?? "—"}/{b.total_records ?? "—"}</td>
                                  <td className="px-3 py-2.5 text-slate-500">{b.uploaded_by_name || "—"}</td>
                                  <td className="px-3 py-2.5 text-slate-500">{b.uploaded_at ? new Date(b.uploaded_at).toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                                  <td className="px-3 py-2.5">
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                                      statusOk ? "bg-emerald-50 text-emerald-700 border-emerald-200" : partial ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-red-50 text-red-700 border-red-200"
                                    }`}>
                                      {statusOk ? "Success" : partial ? "Partial" : "Failed"}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2.5">
                                    <div className="flex items-center justify-end">
                                      <button type="button" onClick={() => deleteBatch(b.batch_id)} className="w-7 h-7 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center text-red-500 hover:text-red-700 transition" title="Delete">
                                        <Trash2 size={13} />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, bg, iconBg, border }) {
  return (
    <div className={`relative p-3 rounded-xl bg-gradient-to-br ${bg} border ${border} overflow-hidden`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide">{label}</div>
          <div className="text-2xl font-bold text-slate-900 mt-1">{value ?? "—"}</div>
        </div>
        <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${iconBg} text-white flex items-center justify-center shadow`}>
          {icon}
        </div>
      </div>
    </div>
  );
}
